/**
 * Phase G+ Feb 2026 — Lohnverrechnung Snapshot-Diff-Dialog
 * --------------------------------------------------------
 * Vergleicht einen gespeicherten Snapshot (`payroll_snapshots`) mit dem
 * aktuell live berechneten Lohnlauf. Liefert dem Steuerberater auf einen
 * Blick "was hat sich seit dem letzten Export geändert".
 *
 * Backend:
 *   GET /api/payroll-lohnliste/snapshots/{year}/{month}     → Liste
 *   GET /api/payroll-lohnliste/snapshot/{id}/diff           → Diff
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtCur } from "@/lib/format";
import {
  X, GitCompare, Loader2, ArrowDown, ArrowUp, MinusCircle, PlusCircle,
  CheckCircle2, AlertCircle,
} from "lucide-react";

const FIELD_LABEL = {
  brutto_total_eur:      { label: "Brutto Total",     unit: "€",  type: "eur" },
  payout_eur:            { label: "Auszahlung",       unit: "€",  type: "eur" },
  advances_deduction_eur:{ label: "Akonto-Abzug",     unit: "€",  type: "eur" },
  dieten_sum_eur:        { label: "Diäten",           unit: "€",  type: "eur" },
  overtime_25_minutes:   { label: "Überstd. 25 %",    unit: "Min", type: "min" },
  overtime_50_minutes:   { label: "Überstd. 50 %",    unit: "Min", type: "min" },
  days_worked:           { label: "Arbeitstage",      unit: "Tage", type: "int" },
};

function fmtField(val, type) {
  if (val === null || val === undefined) return "—";
  if (type === "eur") return fmtCur(val);
  if (type === "min") {
    const m = Math.round(Number(val));
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  }
  return String(val);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-AT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function SnapshotDiffDialog({ year, month, onClose }) {
  const [snapshots, setSnapshots] = useState([]);
  const [snapId, setSnapId] = useState("");
  const [diff, setDiff] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDiff, setLoadingDiff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    api.get(`/payroll-lohnliste/snapshots/${year}/${month}`)
      .then(({ data }) => {
        if (cancelled) return;
        const items = data.items || [];
        // Prefer Lohnliste snapshots over Zulagen; newest first (server already sorts desc).
        const ranked = [...items].sort((a, b) => {
          const aTag = (a.export_kind || a.kind) === "zulagen" ? 1 : 0;
          const bTag = (b.export_kind || b.kind) === "zulagen" ? 1 : 0;
          return aTag - bTag;
        });
        setSnapshots(ranked);
        if (ranked.length > 0) setSnapId(ranked[0].id);
      })
      .catch(() => toast.error("Snapshots konnten nicht geladen werden"))
      .finally(() => !cancelled && setLoadingList(false));
    return () => { cancelled = true; };
  }, [year, month]);

  useEffect(() => {
    if (!snapId) { setDiff(null); return; }
    let cancelled = false;
    setLoadingDiff(true);
    setDiff(null);
    api.get(`/payroll-lohnliste/snapshot/${snapId}/diff`)
      .then(({ data }) => { if (!cancelled) setDiff(data); })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Diff fehlgeschlagen");
      })
      .finally(() => !cancelled && setLoadingDiff(false));
    return () => { cancelled = true; };
  }, [snapId]);

  const monthLabel = `${String(month).padStart(2, "0")}/${year}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="snapshot-diff-dialog"
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
              <GitCompare className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Snapshot-Diff</div>
              <h3 className="font-display text-base font-semibold text-zinc-50">
                Lohnverrechnung {monthLabel} — Vergleich
              </h3>
              <div className="text-[11px] text-zinc-500">
                Vergleicht einen gespeicherten Snapshot mit dem aktuellen Lohnlauf.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="snapshot-diff-close"
            className="text-zinc-400 hover:text-zinc-100 p-2 rounded hover:bg-zinc-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Snapshot selector */}
        <div className="px-6 py-3 border-b border-zinc-800 bg-zinc-900/30">
          {loadingList ? (
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Snapshots laden …
            </div>
          ) : snapshots.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-amber-300">
              <AlertCircle className="w-4 h-4" />
              Noch kein Snapshot für {monthLabel} vorhanden — bitte zuerst die Lohnverrechnung exportieren.
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">
                  Vergleichs-Snapshot
                </label>
                <select
                  value={snapId}
                  onChange={(e) => setSnapId(e.target.value)}
                  data-testid="snapshot-diff-select"
                  className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 min-w-[320px]"
                >
                  {snapshots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {fmtDate(s.created_at)}
                      {(s.export_kind || s.kind) === "zulagen" ? " · Zulagen" : " · Lohnliste"}
                      {s.content_hash ? `  · ${s.content_hash.slice(0, 8)}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-[11px] text-zinc-500 ml-auto self-center">
                {snapshots.length} Snapshot{snapshots.length === 1 ? "" : "s"} verfügbar
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loadingDiff && (
            <div className="flex items-center justify-center gap-2 py-16 text-zinc-400">
              <Loader2 className="w-5 h-5 animate-spin" /> Diff wird berechnet …
            </div>
          )}
          {!loadingDiff && diff && <DiffBody diff={diff} />}
          {!loadingDiff && !diff && !loadingList && snapshots.length === 0 && (
            <EmptyHint />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-zinc-800 bg-zinc-900/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="text-center py-12 text-sm text-zinc-400">
      Für diesen Monat existiert noch kein Snapshot.<br />
      Sobald du die Lohnverrechnung einmal exportierst, wird automatisch ein
      Snapshot angelegt — danach kannst du diesen Diff hier nutzen.
    </div>
  );
}

function DiffBody({ diff }) {
  const rowsWithChanges = useMemo(
    () => (diff.rows || []).filter((r) => (r.changes || []).length > 0),
    [diff]
  );
  const added = (diff.rows || []).filter((r) => r.added);
  const removed = (diff.rows || []).filter((r) => r.removed);

  const totalDeltas = useMemo(() => {
    const c = diff.current_totals || {};
    const p = diff.snapshot_totals || {};
    return [
      ["Brutto Total",    "brutto_total_eur",     "eur"],
      ["Auszahlung",      "payout_eur",           "eur"],
      ["Akonto-Abzug",    "advances_deduction_eur","eur"],
      ["Diäten",          "dieten_sum_eur",       "eur"],
    ].map(([label, key, type]) => {
      const prev = Number(p[key] || 0);
      const curr = Number(c[key] || 0);
      return { label, key, type, prev, curr, delta: curr - prev };
    });
  }, [diff]);

  const noChange = rowsWithChanges.length === 0 && added.length === 0 && removed.length === 0;

  return (
    <div className="space-y-5" data-testid="snapshot-diff-body">
      {/* Header summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Geänderte Fahrer" value={diff.changed_employees || 0}
          icon={GitCompare} tone={diff.changed_employees ? "blue" : "muted"} />
        <StatCard label="Neue Fahrer" value={diff.added || 0}
          icon={PlusCircle} tone={diff.added ? "emerald" : "muted"} />
        <StatCard label="Entfernte Fahrer" value={diff.removed || 0}
          icon={MinusCircle} tone={diff.removed ? "rose" : "muted"} />
        <StatCard label="Snapshot-Hash" value={(diff.prev_hash || "").slice(0, 8) || "—"}
          icon={CheckCircle2} tone="muted" mono />
      </div>

      <div className="text-[11px] text-zinc-500">
        Snapshot vom <span className="text-zinc-300">{fmtDate(diff.prev_created_at)}</span>
        {diff.prev_snapshot_id && (
          <> · ID <span className="font-mono">{diff.prev_snapshot_id.slice(0, 8)}…</span></>
        )}
      </div>

      {noChange && (
        <div
          className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-5 text-center"
          data-testid="snapshot-diff-no-changes"
        >
          <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
          <h4 className="font-display text-base font-semibold text-emerald-200 mt-2">
            Keine Änderungen
          </h4>
          <p className="text-sm text-zinc-300 mt-1">
            Der aktuelle Lohnlauf ist identisch zum gespeicherten Snapshot. Sicher zu exportieren.
          </p>
        </div>
      )}

      {/* Total deltas table */}
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <div className="px-4 py-2.5 bg-zinc-900/50 border-b border-zinc-800 text-xs font-semibold text-zinc-300 uppercase tracking-wider">
          Gesamt-Delta
        </div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-900/30">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Kennzahl</th>
              <th className="px-4 py-2 text-right font-medium">Snapshot</th>
              <th className="px-4 py-2 text-right font-medium">Aktuell</th>
              <th className="px-4 py-2 text-right font-medium">Differenz</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {totalDeltas.map((t) => (
              <tr key={t.key} data-testid={`diff-total-${t.key}`}>
                <td className="px-4 py-2 text-zinc-200">{t.label}</td>
                <td className="px-4 py-2 text-right tabular-nums text-zinc-400">{fmtField(t.prev, t.type)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-zinc-200 font-medium">{fmtField(t.curr, t.type)}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  <DeltaPill delta={t.delta} type={t.type} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-employee changes */}
      {rowsWithChanges.length > 0 && (
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <div className="px-4 py-2.5 bg-zinc-900/50 border-b border-zinc-800 text-xs font-semibold text-zinc-300 uppercase tracking-wider">
            Geänderte Fahrer ({rowsWithChanges.length})
          </div>
          <div className="divide-y divide-zinc-800">
            {rowsWithChanges.map((row) => (
              <EmployeeRow key={row.employee_id} row={row} />
            ))}
          </div>
        </div>
      )}

      {/* Added / removed */}
      {(added.length > 0 || removed.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {added.length > 0 && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 overflow-hidden">
              <div className="px-4 py-2 text-xs font-semibold text-emerald-300 uppercase tracking-wider">
                Neu im Lohnlauf ({added.length})
              </div>
              <ul className="divide-y divide-emerald-500/10 text-sm">
                {added.map((r) => (
                  <li key={r.employee_id} className="px-4 py-2 text-zinc-200">{r.employee_name}</li>
                ))}
              </ul>
            </div>
          )}
          {removed.length > 0 && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 overflow-hidden">
              <div className="px-4 py-2 text-xs font-semibold text-rose-300 uppercase tracking-wider">
                Nicht mehr im Lohnlauf ({removed.length})
              </div>
              <ul className="divide-y divide-rose-500/10 text-sm">
                {removed.map((r) => (
                  <li key={r.employee_id} className="px-4 py-2 text-zinc-200">{r.employee_name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, mono }) {
  const cls = {
    blue:    { bg: "bg-blue-500/10",    bd: "border-blue-500/30",    fg: "text-blue-300"    },
    emerald: { bg: "bg-emerald-500/10", bd: "border-emerald-500/30", fg: "text-emerald-300" },
    rose:    { bg: "bg-rose-500/10",    bd: "border-rose-500/30",    fg: "text-rose-300"    },
    muted:   { bg: "bg-zinc-900/50",    bd: "border-zinc-800",       fg: "text-zinc-400"    },
  }[tone || "muted"];
  return (
    <div className={`rounded-lg border ${cls.bd} ${cls.bg} px-4 py-3`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${cls.fg}`} />
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
      </div>
      <div className={`text-xl font-bold mt-1 ${cls.fg} ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function DeltaPill({ delta, type }) {
  if (Math.abs(Number(delta) || 0) < 0.005) {
    return <span className="text-zinc-500 text-xs">—</span>;
  }
  const positive = Number(delta) > 0;
  const cls = positive
    ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
    : "text-rose-300 bg-rose-500/10 border-rose-500/30";
  const Arrow = positive ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border tabular-nums ${cls}`}>
      <Arrow className="w-3 h-3" />
      {positive ? "+" : "−"}{fmtField(Math.abs(delta), type)}
    </span>
  );
}

function EmployeeRow({ row }) {
  return (
    <div className="px-4 py-3" data-testid={`diff-row-${row.employee_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-zinc-100">{row.employee_name}</div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
          {row.changes.length} Änderung{row.changes.length === 1 ? "" : "en"}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {row.changes.map((c) => {
          const meta = FIELD_LABEL[c.field] || { label: c.field, unit: "", type: "eur" };
          return (
            <div
              key={c.field}
              className="flex items-center justify-between gap-2 bg-zinc-900/40 rounded px-3 py-1.5"
            >
              <span className="text-xs text-zinc-400">{meta.label}</span>
              <div className="flex items-center gap-2 text-xs tabular-nums">
                <span className="text-zinc-500 line-through">{fmtField(c.from, meta.type)}</span>
                <span className="text-zinc-200">{fmtField(c.to, meta.type)}</span>
                <DeltaPill delta={c.delta} type={meta.type} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
